import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { 
  ArrowLeft, Calendar, Clock, MapPin, Users, CheckCircle2, GraduationCap, ArrowRight,
  Loader2, ShoppingCart, Star, Award, Lock, Globe, School, AlertCircle, Check, X,
  Package, Truck, Building, ExternalLink, BookOpen
} from 'lucide-react';
import { Sun } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { KitDetailsSection } from '../../components/KitContentsPreview';
import { toast } from 'sonner';
import axios from 'axios';
import { trackAddToCart, trackViewItem } from '../../utils/ga4';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* Notify Me Button for Coming Soon sections */
function NotifyMeButton({ courseId, flavor }) {
  const [email, setEmail] = React.useState('');
  const [submitted, setSubmitted] = React.useState(false);
  const handleNotify = async () => {
    if (!email) return;
    try {
      await fetch(`${API_URL}/api/contact/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'Notify Me', email, subject: `Notify: ${flavor} sessions`, message: `Course: ${courseId}\nFlavor: ${flavor}\nEmail: ${email}` })
      });
    } catch {}
    setSubmitted(true);
  };
  if (submitted) return <p className="text-xs text-green-600 font-semibold">✓ We'll notify you when sessions are posted!</p>;
  return (
    <div className="flex gap-2 justify-center max-w-xs mx-auto">
      <input type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)}
        className="flex-1 px-3 py-1.5 text-xs rounded-md border border-gray-200 focus:outline-none focus:border-primary" />
      <button onClick={handleNotify} className="px-3 py-1.5 text-xs font-semibold bg-primary text-white rounded-md hover:bg-primary/90">Notify Me</button>
    </div>
  );
}

export default function CourseDetailPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, api, user } = useAuth();
  const { addToCart } = useCart();
  
  const [course, setCourse] = useState(null);
  const [sections, setSections] = useState([]);
  const [campSessions, setCampSessions] = useState([]);
  const [learners, setLearners] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [afterSchoolClasses, setAfterSchoolClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedLearners, setSelectedLearners] = useState([]); // Changed to array for multiple selection
  const [addingToCart, setAddingToCart] = useState(false);
  const [eligibility, setEligibility] = useState({}); // { sectionId: { learnerId: { eligible, reason } } }
  const [includeKit, setIncludeKit] = useState(false);
  const [kitDeliveryMethod, setKitDeliveryMethod] = useState('pickup');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [courseRes, sectionsRes, campSessionsRes] = await Promise.all([
          axios.get(`${API_URL}/api/courses/${courseId}`),
          axios.get(`${API_URL}/api/sections/`, { params: { course: courseId } }),
          axios.get(`${API_URL}/api/courses/${courseId}/camp_sessions/`).catch(() => ({ data: { results: [] } })),
        ]);
        setCourse(courseRes.data);
        const sectionsData = sectionsRes.data.results || sectionsRes.data;
        const courseSections = sectionsData.filter(s => s.course === courseId || s.course_id === courseId);
        setSections(courseSections.length > 0 ? courseSections : sectionsData);
        setCampSessions(campSessionsRes.data?.results || []);

        // GA4 Ecommerce — view_item on course detail view.
        try {
          const firstSection = (courseSections.length > 0 ? courseSections : sectionsData)[0];
          trackViewItem({
            id: courseRes.data.id || courseId,
            name: courseRes.data.title || courseRes.data.name,
            category: courseRes.data.category || 'After-School Program',
            location: firstSection?.location_name || null,
            grade_band: courseRes.data.grade_band,
            price: Number(firstSection?.price || courseRes.data.price || 0),
          });
        } catch { /* analytics never breaks UX */ }

        // Fetch lesson outlines
        axios.get(`${API_URL}/api/courses/${courseId}/lessons/`)
          .then(r => setLessons(r.data || []))
          .catch(() => {});

        // Fetch after-school schedule and filter for this course
        fetch('/afterschool_schedule.json')
          .then(r => r.json())
          .then(data => {
            const matching = data.filter(c => c.course === courseRes.data.title);
            setAfterSchoolClasses(matching);
          })
          .catch(() => {});

        // Fetch learners if authenticated as parent
        if (isAuthenticated && user?.role === 'parent') {
          try {
            const learnersRes = await api.get('/learners/');
            const learnersData = learnersRes.data.results || learnersRes.data;
            setLearners(learnersData);
            
            // Check eligibility for all sections and learners
            if (learnersData.length > 0 && courseSections.length > 0) {
              const sectionIds = courseSections.map(s => s.id);
              const learnerIds = learnersData.map(l => l.id);
              
              try {
                const eligibilityRes = await api.post('/sections/check_eligibility_bulk/', {
                  section_ids: sectionIds,
                  learner_ids: learnerIds
                });
                setEligibility(eligibilityRes.data);
              } catch (e) {
                console.error('Failed to check eligibility:', e);
              }
            }
          } catch (e) {
            console.error('Failed to fetch learners:', e);
          }
        }
      } catch (error) {
        console.error('Failed to fetch course:', error);
        toast.error('Failed to load course details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [courseId, isAuthenticated, api, user]);

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to enroll');
      navigate('/login', { state: { from: `/courses/${courseId}` } });
      return;
    }

    if (!selectedSection || selectedLearners.length === 0) {
      toast.error('Please select a session and at least one learner');
      return;
    }

    // Determine if kit should be included based on section's kit requirement
    const section = sections.find(s => s.id === selectedSection);
    const sectionKitReq = section?.kit_requirement;
    const shouldIncludeKit = sectionKitReq === 'required' || (course.has_kit && includeKit);
    
    // For online courses with shipped kits, delivery method should be 'ship'
    const deliveryMethod = section?.delivery_type === 'online' ? 'ship' : kitDeliveryMethod;

    setAddingToCart(true);
    try {
      // Add each selected learner to cart
      for (const learnerId of selectedLearners) {
        // Check eligibility before adding
        const sectionEligibility = eligibility[selectedSection];
        if (sectionEligibility) {
          const learnerEligibility = sectionEligibility.learners?.[learnerId];
          if (learnerEligibility && !learnerEligibility.eligible) {
            const learner = learners.find(l => l.id === learnerId);
            toast.error(`${learner?.first_name || 'Learner'} is not eligible: ${learnerEligibility.reason}`);
            continue;
          }
        }
        await addToCart(selectedSection, learnerId, shouldIncludeKit, deliveryMethod);
        // GA4 Ecommerce — add_to_cart per learner so quantity & revenue
        // are attributable per registration line.
        try {
          trackAddToCart({
            id: selectedSection,
            name: section?.section_title || course?.title || course?.name,
            category: course?.category || 'After-School Program',
            location: section?.location_name || null,
            grade_band: course?.grade_band,
            price: Number(section?.price || course?.price || 0),
          });
        } catch { /* analytics never breaks UX */ }
      }
    } catch (error) {
      // Error handled in cart context
    } finally {
      setAddingToCart(false);
    }
  };

  const handleLearnerToggle = (learnerId, isEligible) => {
    if (!isEligible) return;
    
    setSelectedLearners(prev => {
      if (prev.includes(learnerId)) {
        return prev.filter(id => id !== learnerId);
      } else {
        return [...prev, learnerId];
      }
    });
  };

  const getRestrictionIcon = (section) => {
    if (section.enrollment_restriction === 'school_only') {
      return <School className="h-4 w-4 text-blue-600" />;
    }
    if (section.enrollment_restriction === 'district_only') {
      return <Lock className="h-4 w-4 text-purple-600" />;
    }
    return <Globe className="h-4 w-4 text-green-600" />;
  };

  const getRestrictionBadge = (section) => {
    if (section.enrollment_restriction === 'school_only') {
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs">
          <School className="h-3 w-3 mr-1" />
          {section.restricted_to_school_name || 'School Only'}
        </Badge>
      );
    }
    if (section.enrollment_restriction === 'district_only') {
      return (
        <Badge variant="outline" className="bg-purple-50 text-purple-700 text-xs">
          <Lock className="h-3 w-3 mr-1" />
          {section.restricted_to_district_name || 'District Only'}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-green-50 text-green-700 text-xs">
        <Globe className="h-3 w-3 mr-1" />
        Open Enrollment
      </Badge>
    );
  };

  const getLearnerEligibilityForSection = (sectionId, learnerId) => {
    const sectionData = eligibility[sectionId];
    if (!sectionData || !sectionData.learners) return null;
    return sectionData.learners[learnerId];
  };

  const getEligibleLearnersForSection = (sectionId) => {
    const sectionData = eligibility[sectionId];
    if (!sectionData || !sectionData.learners) return learners;
    return learners.filter(l => {
      const elig = sectionData.learners[l.id];
      return !elig || elig.eligible;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="container-app py-12 text-center">
        <h1 className="text-2xl font-bold mb-4">Course not found</h1>
        <Link to="/catalog">
          <Button>Back to Catalog</Button>
        </Link>
      </div>
    );
  }

  const selectedSectionData = sections.find(s => s.id === selectedSection);
  const eligibleLearners = selectedSection ? getEligibleLearnersForSection(selectedSection) : learners;

  return (
    <div className="pb-20">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-800 via-slate-900 to-primary/30">
        <div className="container-app relative z-10 py-6 md:py-8">
          {/* Back to Catalog */}
          <Link to="/catalog" className="inline-flex items-center text-sm text-white/60 hover:text-white mb-6 transition-colors" data-testid="back-to-catalog">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Catalog
          </Link>

          <div className={`grid gap-8 items-center ${course.thumbnail_url ? 'grid-cols-1 lg:grid-cols-5' : 'grid-cols-1'}`}>
            {/* Text content */}
            <div className={course.thumbnail_url ? 'lg:col-span-3' : 'max-w-2xl'}>
              <div className="flex flex-wrap gap-2 mb-5">
                <Badge className="bg-yellow-400 text-yellow-900 hover:bg-yellow-400 capitalize font-semibold">
                  {course.skill_level}
                </Badge>
                {course.grade_bands?.map(grade => (
                  <Badge key={grade} variant="outline" className="border-white/40 text-white/90 bg-white/10 backdrop-blur-sm">
                    {grade}
                  </Badge>
                ))}
                {course.category && (
                  <Badge variant="outline" className="border-white/40 text-white/90 bg-white/10 backdrop-blur-sm capitalize">
                    {course.category}
                  </Badge>
                )}
              </div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white" data-testid="course-title">
                {course.title}
              </h1>
              <p className="text-base md:text-lg text-white/80 leading-relaxed">
                {course.description}
              </p>
              
              {/* Quick stats */}
              <div className="flex flex-wrap gap-4 mt-6">
                {sections.length > 0 && (
                  <div className="flex items-center gap-2 text-white/70 text-sm">
                    <Calendar className="h-4 w-4" />
                    <span>{sections.length} session{sections.length !== 1 ? 's' : ''} available</span>
                  </div>
                )}
              </div>
            </div>

            {/* Image */}
            {course.thumbnail_url && (
              <div className="lg:col-span-2 flex justify-center lg:justify-end">
                <img
                  src={course.thumbnail_url}
                  alt={course.title}
                  className="rounded-2xl shadow-2xl max-h-72 w-auto object-contain ring-1 ring-white/10"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-app">
          {/* Top: What You'll Learn + Lessons — horizontal row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {/* Learning Outcomes */}
            {course.learning_outcomes?.length > 0 && (
              <Card className="card-dashboard">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Award className="h-5 w-5 text-primary" />
                    What You'll Learn
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {course.learning_outcomes.map((outcome, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm" data-testid={`outcome-${index}`}>
                          <CheckCircle2 className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                          <span>{outcome}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Lesson Outlines */}
              {lessons.length > 0 && (
                <Card className="card-dashboard" data-testid="course-lessons">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <BookOpen className="h-5 w-5 text-blue-600" />
                      Session Outline ({lessons.length} sessions)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {lessons.map((lesson) => (
                        <div key={lesson.id} className="border-l-2 border-blue-200 pl-3" data-testid={`lesson-${lesson.lesson_number}`}>
                          <p className="text-sm font-semibold text-gray-900">
                            <span className="text-blue-500 mr-1">{lesson.lesson_number}.</span>
                            {lesson.title}
                          </p>
                          {lesson.objectives?.length > 0 && (
                            <ul className="mt-1 space-y-0.5">
                              {lesson.objectives.map((obj, i) => (
                                <li key={i} className="text-xs text-gray-500 flex items-start gap-1">
                                  <CheckCircle2 className="h-3 w-3 text-blue-400 flex-shrink-0 mt-0.5" />
                                  {obj}
                                </li>
                              ))}
                            </ul>
                          )}
                          {lesson.materials?.length > 0 && (
                            <p className="text-[10px] text-gray-400 mt-1">
                              Materials: {lesson.materials.join(', ')}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Skills Rubric */}
              {course.rubric_items?.length > 0 && (
                <Card className="card-dashboard">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Star className="h-5 w-5 text-secondary" />
                      Skills Covered
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {course.rubric_items.map((item, index) => (
                        <Badge key={index} variant="secondary" className="text-sm" data-testid={`skill-${index}`}>
                          {item}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

            </div>

          {/* Full-width: Available Sessions - Side by Side */}
          <div>
              <Card className="card-dashboard">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-primary" />
                    Available Sessions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                    {/* LEFT: Summer Camp Sessions */}
                    <div>
                      <h4 className="text-base font-bold mb-3 flex items-center gap-2">
                        <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center"><Sun className="w-5 h-5 text-amber-600" /></span>
                        Summer Camp
                      </h4>

                      {/* Explorer Pricing Banner */}
                      {campSessions.length > 0 && (
                        <div className="rounded-xl bg-gradient-to-r from-rsc-navy to-[#1a3a6d] p-4 mb-4">
                          <p className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-1">Summer Discount — <span className="line-through text-white/30">$150</span> → $119/course</p>
                          <div className="grid grid-cols-3 gap-2">
                            <div className="text-center p-2 rounded-lg bg-white/10">
                              <p className="text-xl font-extrabold text-white">$119</p>
                              <p className="text-[10px] text-white/50">per day (save 21%)</p>
                            </div>
                            <div className="text-center p-2 rounded-lg bg-white/10 border border-emerald-400/30">
                              <p className="text-xl font-extrabold text-emerald-400">$375</p>
                              <p className="text-[10px] text-white/50">4-day bundle</p>
                            </div>
                            <div className="text-center p-2 rounded-lg bg-white/10 border border-rsc-yellow/30">
                              <p className="text-xl font-extrabold text-rsc-yellow">$450</p>
                              <p className="text-[10px] text-white/50">5-day bundle</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="space-y-4">
                    {campSessions.length > 0 ? (
                      <div className="space-y-4">
                        {campSessions.map((cs) => {
                          const catColors = { science: 'bg-rose-500', coding: 'bg-blue-500', robotics: 'bg-orange-500', engineering: 'bg-emerald-500', math: 'bg-purple-500' };
                          const catEmojis = { science: '🔬', coding: '💻', robotics: '🤖', engineering: '⚙️', math: '🔢' };
                          const is4DayLoc = cs.location_name?.includes('Bednarcik') || cs.location_name?.includes('Plank');
                          const isHalfDay = cs.location_name?.includes('Library') || cs.location_name?.includes('95th');
                          const campLabel = isHalfDay ? 'Half-Day Camp' : is4DayLoc ? '4-Day Camp (Mon–Thu)' : '5-Day Camp (Mon–Fri)';
                          const displayPrice = is4DayLoc ? (cs.price_4_day || cs.price_full_camp) : cs.price_full_camp;
                          const displayDays = cs.days ? (is4DayLoc ? cs.days.filter((_, i) => i < 4) : cs.days) : [];
                          const flavorColors = isHalfDay ? 'border-pink-200 bg-pink-50/30' : is4DayLoc ? 'border-amber-200 bg-amber-50/30' : 'border-blue-200 bg-blue-50/30';
                          return (
                            <div key={cs.id} className={`rounded-xl border ${flavorColors} overflow-hidden`}>
                              {/* Header */}
                              <div className="p-3 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-9 h-9 rounded-lg bg-rsc-navy flex flex-col items-center justify-center flex-shrink-0">
                                    <span className="text-[10px] text-white/50">{cs.start_date ? new Date(cs.start_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' }) : ''}</span>
                                  </div>
                                  <div>
                                    <p className="text-sm font-bold">
                                      {cs.start_date && new Date(cs.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                      {cs.end_date && ` – ${new Date(cs.end_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                                    </p>
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                      <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" /> {cs.start_time}–{cs.end_time}</span>
                                      <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {cs.location_name}</span>
                                      <Badge variant="outline" className="text-[10px]">{campLabel}</Badge>
                                      {cs.grade_bands?.map(g => <Badge key={g} variant="secondary" className="text-[10px]">{g}</Badge>)}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-lg font-bold text-rsc-navy">${displayPrice}{isHalfDay ? '' : '/wk'}</p>
                                  <p className="text-[10px] text-emerald-600">${cs.price_per_day}/day</p>
                                </div>
                              </div>
                              {/* Weekly schedule strip */}
                              {displayDays.length > 0 && (
                                <div className="px-3 pb-3">
                                  <div className={`grid ${is4DayLoc ? 'grid-cols-4' : 'grid-cols-5'} gap-1.5`}>
                                    {displayDays.map((day, idx) => {
                                      const isCurrentCourse = day.course_title && course?.title && day.course_title.toLowerCase().includes(course.title.toLowerCase().split(':')[0].toLowerCase());
                                      // Each day_number can appear twice (AM + PM variants), so compose
                                      // a unique key from day_number + index rather than day_number alone.
                                      return (
                                        <div key={`${day.day_number}-${idx}`} className="block cursor-pointer" onClick={() => {
                                          navigate(`/register?type=camp&id=${cs.camp_id}&section=${cs.id}&day=${day.day_number}`);
                                        }}>
                                          <div className={`rounded-lg border p-2 text-center transition-all hover:border-primary/50 ${isCurrentCourse ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-gray-100 bg-gray-50/50'}`}>
                                            <div className="text-[9px] font-bold text-gray-400 uppercase">{day.day_name?.slice(0,3)}</div>
                                            <div className={`w-7 h-7 rounded-md ${catColors[day.category] || 'bg-gray-400'} flex items-center justify-center mx-auto my-1`}>
                                              <span className="text-xs">{catEmojis[day.category] || '📚'}</span>
                                            </div>
                                            <p className={`text-[9px] font-bold ${isCurrentCourse ? 'text-primary' : catColors[day.category]?.replace('bg-', 'text-').replace('-500', '-600') || 'text-gray-600'}`}>{day.category_label}</p>
                                            <p className="text-[8px] text-gray-500 line-clamp-2 leading-tight mt-0.5">{day.course_title}</p>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              {/* Location + spots + Register button */}
                              <div className="px-3 pb-3">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> {cs.location_name}</span>
                                  <span className="text-xs text-emerald-600 font-semibold">{cs.spots_remaining} spots left</span>
                                </div>
                                <Button size="sm" className="w-full bg-rsc-yellow text-rsc-navy font-bold rounded-full text-sm" onClick={() => {
                                  navigate(`/register?type=camp&id=${cs.camp_id}&section=${cs.id}`);
                                }}>
                                  Register for This Week <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-4 rounded-lg border border-dashed border-amber-200 bg-amber-50/30 text-center">
                        <Sun className="w-6 h-6 text-amber-300 mx-auto mb-1" />
                        <p className="text-sm font-semibold text-amber-700">No summer camp sessions for this course yet</p>
                        <p className="text-xs text-muted-foreground mb-2">Get notified when new camp dates are scheduled</p>
                        <NotifyMeButton courseId={courseId} flavor="summer_camp" />
                      </div>
                    )}
                  </div>
                </div>

                    {/* RIGHT: After-School / Weekend Sessions */}
                    <div>
                      <h4 className="text-base font-bold mb-3 flex items-center gap-2">
                        <span className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center"><BookOpen className="w-5 h-5 text-blue-600" /></span>
                        After-School / Weekend
                      </h4>

                      {afterSchoolClasses.length > 0 ? (
                        <div className="space-y-3">
                          {afterSchoolClasses.map((cls, i) => (
                            <div key={i} className="rounded-xl border border-blue-100 bg-blue-50/30 p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex flex-col items-center justify-center flex-shrink-0">
                                    <span className="text-xs font-bold text-blue-700">{cls.day?.slice(0,3)}</span>
                                  </div>
                                  <div>
                                    <p className="text-sm font-bold text-gray-900">{cls.day}s</p>
                                    <p className="text-xs text-gray-500">{cls.time}</p>
                                  </div>
                                </div>
                                <p className="text-2xl font-bold text-rsc-navy">${cls.price || 150}</p>
                              </div>
                              <div className="space-y-1 text-sm text-gray-600 mb-2">
                                <div className="flex items-center gap-2"><GraduationCap className="w-3.5 h-3.5 text-rsc-navy" /> <span className="font-bold text-rsc-navy">Grades {cls.grade}</span></div>
                                <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-gray-400" /> {cls.location}</div>
                                <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-gray-400" /> {cls.dates}</div>
                              </div>
                              {cls.district && (
                                <div className="p-2 rounded-lg bg-amber-50 border border-amber-200 mb-2 flex items-center gap-2">
                                  <AlertCircle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                                  <p className="text-xs text-amber-700 font-medium">{cls.eligibility}</p>
                                </div>
                              )}
                              <Link to="/summer-camps">
                                <Button size="sm" className="w-full bg-blue-600 text-white font-bold rounded-full text-sm">
                                  View Summer Camps <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                                </Button>
                              </Link>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-400">
                          <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-30" />
                          <p className="text-sm">No sessions for this course yet.</p>
                          <Link to="/summer-camps" className="text-xs text-blue-500 hover:underline mt-1 inline-block">Browse summer camps</Link>
                        </div>
                      )}
                    </div>

                  </div>
                </CardContent>
              </Card>
          </div>

          {/* Full Width: Enroll Now */}
          <div className="mt-6">
              <Card className="card-dashboard">
                <CardHeader>
                  <CardTitle>Enroll Now</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!isAuthenticated ? (
                    <div className="text-center py-4">
                      <p className="text-muted-foreground mb-4">Log in to enroll your child</p>
                      <Link to="/login" state={{ from: `/courses/${courseId}` }}>
                        <Button className="w-full" data-testid="login-to-enroll-btn">Log In to Enroll</Button>
                      </Link>
                    </div>
                  ) : learners.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-muted-foreground mb-4">Add a learner to enroll</p>
                      <Link to="/parent/learners">
                        <Button className="w-full">Add Learner</Button>
                      </Link>
                    </div>
                  ) : (
                    <>
                      {/* Session Selection */}
                      <div>
                        <label className="text-sm font-medium mb-2 block">Select Session</label>
                        <Select value={selectedSection} onValueChange={(v) => { 
                    setSelectedSection(v); 
                    setSelectedLearners([]); 
                    // Auto-set kit checkbox based on section's kit requirement
                    const section = sections.find(s => s.id === v);
                    const sectionKitReq = section?.kit_requirement;
                    if (sectionKitReq === 'required') {
                      setIncludeKit(true);
                    } else {
                      setIncludeKit(false);
                    }
                  }}>
                          <SelectTrigger data-testid="section-select">
                            <SelectValue placeholder="Choose a session" />
                          </SelectTrigger>
                          <SelectContent>
                            {sections.map(section => (
                              <SelectItem key={section.id} value={section.id} disabled={section.enrolled_count >= section.capacity && !section.waitlist_enabled}>
                                {section.location_name} - {section.start_date}
                                {section.delivery_type === 'online' && ' (Online)'}
                                {section.location_external_purchase && ' ↗ External'}
                                {section.enrolled_count >= section.capacity ? (section.waitlist_enabled ? ' (Waitlist)' : ' (Full)') : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Learner Selection - Using Checkboxes */}
                      {selectedSection && (
                        <div>
                          <label className="text-sm font-medium mb-3 block">Select Children to Enroll</label>
                          <div className="space-y-3">
                            {learners.map(learner => {
                              const elig = getLearnerEligibilityForSection(selectedSection, learner.id);
                              const isEligible = !elig || elig.eligible;
                              const isSelected = selectedLearners.includes(learner.id);
                              
                              return (
                                <div 
                                  key={learner.id}
                                  className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                                    isSelected 
                                      ? 'border-primary bg-primary/5' 
                                      : isEligible 
                                        ? 'border-border hover:border-primary/50 cursor-pointer' 
                                        : 'border-border bg-muted/30'
                                  }`}
                                  onClick={() => handleLearnerToggle(learner.id, isEligible)}
                                  data-testid={`learner-option-${learner.id}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <Checkbox 
                                      id={`learner-${learner.id}`}
                                      checked={isSelected}
                                      disabled={!isEligible}
                                      onCheckedChange={() => handleLearnerToggle(learner.id, isEligible)}
                                      data-testid={`learner-checkbox-${learner.id}`}
                                    />
                                    <div>
                                      <Label 
                                        htmlFor={`learner-${learner.id}`} 
                                        className={`font-medium cursor-pointer ${!isEligible ? 'text-muted-foreground' : ''}`}
                                      >
                                        {learner.first_name} {learner.last_name}
                                      </Label>
                                      {learner.grade && (
                                        <p className="text-xs text-muted-foreground">Grade {learner.grade}</p>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Eligibility Badge */}
                                  {isEligible ? (
                                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                      Eligible
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                                      Ineligible
                                    </Badge>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          
                          {/* Show eligibility reasons for ineligible learners */}
                          {learners.some(l => {
                            const elig = getLearnerEligibilityForSection(selectedSection, l.id);
                            return elig && !elig.eligible;
                          }) && (
                            <Alert className="mt-3 bg-amber-50 border-amber-200">
                              <AlertCircle className="h-4 w-4 text-amber-600" />
                              <AlertDescription className="text-amber-700 text-sm">
                                Some children are ineligible due to school/district restrictions for this session.
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>
                      )}

                      {/* Product Selection */}
                      {selectedSection && course.has_kit && selectedSectionData?.kit_requirement !== 'none' && selectedSectionData?.kit_requirement !== 'included' && (
                        <div className="border border-border rounded-lg p-4 space-y-3" data-testid="kit-selection">
                          <div className="flex items-start gap-3">
                            <Package className="h-5 w-5 text-primary mt-0.5" />
                            <div className="flex-1">
                              <h4 className="font-medium">{course.kit_details?.name || 'Product'}</h4>
                              {selectedSectionData?.kit_requirement === 'required' ? (
                                <div className="space-y-1">
                                  <Badge className="mt-1 bg-blue-100 text-blue-700">Required for this session</Badge>
                                  <p className="text-xs text-muted-foreground">
                                    This product is required and will be added to your order automatically.
                                  </p>
                                </div>
                              ) : (
                                <Badge variant="outline" className="mt-1">Optional Add-on</Badge>
                              )}
                            </div>
                            <div className="text-right">
                              <span className="font-bold text-primary">
                                +${(Number(course.kit_details?.price) || 0).toFixed(2)}
                              </span>
                              {selectedSectionData?.kit_requirement === 'required' && (
                                <p className="text-xs text-muted-foreground">added to total</p>
                              )}
                            </div>
                          </div>
                          
                          {selectedSectionData?.kit_requirement === 'optional' && (
                            <div className="flex items-center space-x-2">
                              <Checkbox 
                                id="include-kit" 
                                checked={includeKit}
                                onCheckedChange={setIncludeKit}
                                data-testid="include-kit-checkbox"
                              />
                              <Label htmlFor="include-kit" className="text-sm cursor-pointer">
                                Add {course.kit_details?.name || 'product'} to my order
                              </Label>
                            </div>
                          )}

                          {/* Kit delivery method - only show for in-person courses with kits */}
                          {(selectedSectionData?.kit_requirement === 'required' || includeKit) && selectedSectionData?.delivery_type !== 'online' && (
                            <div className="space-y-2 pt-2 border-t border-border">
                              <Label className="text-sm font-medium">Kit Delivery</Label>
                              <RadioGroup 
                                value={kitDeliveryMethod} 
                                onValueChange={setKitDeliveryMethod}
                                className="flex flex-col gap-2"
                              >
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="pickup" id="pickup" data-testid="delivery-pickup" />
                                  <Label htmlFor="pickup" className="flex items-center gap-2 text-sm cursor-pointer">
                                    <Building className="h-4 w-4" />
                                    Pick up at class location
                                  </Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="ship" id="ship" data-testid="delivery-ship" />
                                  <Label htmlFor="ship" className="flex items-center gap-2 text-sm cursor-pointer">
                                    <Truck className="h-4 w-4" />
                                    Ship to my address
                                  </Label>
                                </div>
                              </RadioGroup>
                            </div>
                          )}

                          {/* Online course notice */}
                          {(selectedSectionData?.kit_requirement === 'required' || includeKit) && selectedSectionData?.delivery_type === 'online' && (
                            <Alert className="bg-blue-50 border-blue-200">
                              <Truck className="h-4 w-4 text-blue-600" />
                              <AlertDescription className="text-blue-700 text-sm">
                                Kit will be shipped to your address for this online course.
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>
                      )}

                      {/* Product Included Banner */}
                      {selectedSection && course.has_kit && selectedSectionData?.kit_requirement === 'included' && (
                        <div className="border border-green-200 bg-green-50 rounded-lg p-4 flex items-center gap-3" data-testid="product-included-banner">
                          <Package className="h-5 w-5 text-green-600" />
                          <div>
                            <p className="font-medium text-green-800">{course.kit_details?.name || 'Product'} Included</p>
                            <p className="text-xs text-green-600">Take-home product included with this course at no extra charge.</p>
                          </div>
                        </div>
                      )}

                      {/* Selected Session Summary */}
                      {selectedSectionData && (
                        <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Session:</span>
                            <span className="font-medium">{selectedSectionData.location_name}</span>
                          </div>
                          {selectedSectionData.delivery_type === 'online' && (
                            <div className="flex justify-between text-sm">
                              <span>Format:</span>
                              <Badge variant="outline" className="text-xs">
                                <Globe className="h-3 w-3 mr-1" />
                                Online
                              </Badge>
                            </div>
                          )}
                          <div className="flex justify-between text-sm">
                            <span>Dates:</span>
                            <span>{selectedSectionData.start_date} - {selectedSectionData.end_date}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Schedule:</span>
                            <span>{Array.isArray(selectedSectionData.meeting_days) ? selectedSectionData.meeting_days.join(', ') : (selectedSectionData.meeting_days || 'TBD')}</span>
                          </div>
                          <hr className="my-2" />
                          <div className="flex justify-between text-sm">
                            <span>Course:</span>
                            <span>${(Number(selectedSectionData.price) || 0).toFixed(2)}</span>
                          </div>
                          {course.has_kit && (selectedSectionData?.kit_requirement === 'required' || includeKit) && (
                            <div className="flex justify-between text-sm text-primary">
                              <span>+ STEM Kit:</span>
                              <span>${(Number(course.kit_details?.price) || 0).toFixed(2)}</span>
                            </div>
                          )}
                          <hr className="my-2" />
                          <div className="flex justify-between font-bold text-lg">
                            <span>Total:</span>
                            <span>
                              ${(
                                (Number(selectedSectionData.price) || 0) + 
                                ((course.has_kit && (selectedSectionData?.kit_requirement === 'required' || includeKit)) 
                                  ? (Number(course.kit_details?.price) || 0) 
                                  : 0)
                              ).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Add to Cart / External Purchase Button */}
                      {selectedSectionData?.location_external_purchase && selectedSectionData?.location_external_purchase_url ? (
                        <a
                          href={selectedSectionData.location_external_purchase_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <Button 
                            className="w-full bg-purple-600 hover:bg-purple-700" 
                            size="lg"
                            disabled={!selectedSection}
                            data-testid="external-purchase-btn"
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Register on {selectedSectionData.location_name || 'Partner Site'}
                          </Button>
                          <p className="text-xs text-muted-foreground text-center mt-1.5">
                            Registration for this location is handled by {selectedSectionData.location_name}
                          </p>
                        </a>
                      ) : (
                        <>
                          {selectedSectionData && selectedSectionData.enrolled_count >= selectedSectionData.capacity && selectedSectionData.waitlist_enabled ? (
                            <div data-testid="waitlist-section">
                              <Alert className="mb-3 border-amber-200 bg-amber-50">
                                <AlertCircle className="h-4 w-4 text-amber-600" />
                                <AlertDescription className="text-amber-700 text-sm">
                                  This session is full. You can join the waitlist and we'll notify you when a spot opens up.
                                  {selectedSectionData.waitlist_count > 0 && ` (${selectedSectionData.waitlist_count} on waitlist)`}
                                </AlertDescription>
                              </Alert>
                              <Button 
                                className="w-full bg-amber-500 hover:bg-amber-600 text-white" 
                                size="lg"
                                onClick={handleAddToCart}
                                disabled={!selectedSection || selectedLearners.length === 0 || addingToCart}
                                data-testid="join-waitlist-btn"
                              >
                                {addingToCart ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <Users className="h-4 w-4 mr-2" />
                                )}
                                Join Waitlist
                              </Button>
                            </div>
                          ) : (
                            <Button 
                              className="w-full" 
                              size="lg"
                              onClick={handleAddToCart}
                              disabled={!selectedSection || selectedLearners.length === 0 || addingToCart}
                              data-testid="add-to-cart-btn"
                            >
                              {addingToCart ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <ShoppingCart className="h-4 w-4 mr-2" />
                              )}
                              {selectedLearners.length > 1 
                                ? `Add ${selectedLearners.length} Enrollments to Cart`
                                : 'Add to Cart'
                              }
                            </Button>
                          )}
                        </>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
        </div>
      </section>

      {/* End of content */}
    </div>
  );
}
